import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleUser, ClipboardList, Home, Map, Plus } from 'lucide-react';
import { cn } from '@/lib/cn';

const LEFT_TABS = [
  { to: '/', end: true, icon: Home, labelKey: 'nav.home' },
  { to: '/map', end: false, icon: Map, labelKey: 'nav.map' },
] as const;
const RIGHT_TABS = [
  { to: '/reports', end: false, icon: ClipboardList, labelKey: 'nav.reports' },
  { to: '/profile', end: false, icon: CircleUser, labelKey: 'nav.profile' },
] as const;

/**
 * Persistent bottom navigation with the "+ Report Issue" action DOCKED
 * in the center slot (final-design blueprint — the primary action must
 * never float over screen content). Layout:
 * Home · Map · [＋] · My Reports · Profile.
 *
 * Root screens must pad their bottom with
 * `pb-[calc(6rem+env(safe-area-inset-bottom))]`: the bar is 64px + the
 * home-indicator inset, and the raised center button adds another
 * ~12px above the bar top (8px rise + 4px ring).
 *
 * The five columns are `minmax(0, 1fr)`, so a cell is 64px wide on a
 * 320px phone and a label wider than that used to spill out of it — in
 * French, "Mes Signalements" ran 84px and reached across the centre slot
 * into the raised "+", where the two overlapped and one of them took the
 * tap. English is short enough to hide it. Labels are therefore boxed:
 * see `Tab` below.
 */
export function BottomNav(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto grid h-16 max-w-md grid-cols-5">
        {LEFT_TABS.map((tab) => (
          <Tab key={tab.to} {...tab} />
        ))}
        {/* Center-docked primary action (spec Part II §4 — the app's
            primary CTA), raised above the bar per the blueprint. No text
            label (Peyman round-3): the circle alone is the tap target,
            centered in the slot with a slight rise so it still docks over
            the bar edge. The aria-label keeps it accessible. */}
        <button
          type="button"
          onClick={() => navigate('/report')}
          aria-label={t('nav.reportIssue')}
          className="group relative flex items-center justify-center"
        >
          <span
            aria-hidden
            className="flex h-14 w-14 -translate-y-3 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-4 ring-card transition group-hover:opacity-90"
          >
            <Plus className="h-7 w-7" aria-hidden />
          </span>
        </button>
        {RIGHT_TABS.map((tab) => (
          <Tab key={tab.to} {...tab} />
        ))}
      </div>
    </nav>
  );
}

function Tab({
  to,
  end,
  icon: Icon,
  labelKey,
}: {
  to: string;
  end: boolean;
  icon: typeof Home;
  labelKey: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'relative flex min-w-0 flex-col items-center justify-center gap-0.5 overflow-hidden font-medium',
          isActive ? 'text-primary' : 'text-muted',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className="h-5 w-5 shrink-0" aria-hidden />
          {/* Fixed two-line box: `overflow-hidden` on the link plus
              `line-clamp-2` here means no translation of any length can
              reach another cell, `break-words` means a single word too
              wide to fit breaks instead of spilling, and pinning the box
              to two lines keeps every tab's icon on the same baseline
              whether its label wraps or not. 11px rather than 12 so the
              longest shipping label ("Signalements") still wraps whole
              at 320px instead of being clipped mid-word. */}
          <span className="flex h-7 w-full items-center justify-center px-0.5">
            <span className="line-clamp-2 min-w-0 hyphens-auto text-center text-xs leading-tight break-words">
              {t(labelKey)}
            </span>
          </span>
          {isActive && (
            <span aria-hidden className="absolute top-1 h-1 w-1 rounded-full bg-primary" />
          )}
        </>
      )}
    </NavLink>
  );
}
